import { useCallback, useEffect, useRef, useState } from 'react';

/*
 * Loads Stripe.js straight from Stripe's own CDN, once per page, rather than
 * adding `@stripe/stripe-js` as an npm dependency. This is Stripe's own
 * documented lightweight integration path and needs no build-tool change —
 * appropriate here because `require_card_for_signup` (see
 * claude/trial-and-stripe-payment-flow-2026-09-20.md) is admin-controlled
 * and OFF by default, so most environments never load this script at all
 * (see useStripeCardElement below: it does nothing until a publishable key
 * is actually passed in).
 */
let stripeJsPromise = null;
function loadStripeJs() {
  if (typeof window !== 'undefined' && window.Stripe) return Promise.resolve(window.Stripe);
  if (stripeJsPromise) return stripeJsPromise;
  stripeJsPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;
    script.onload = () => resolve(window.Stripe);
    script.onerror = () => reject(new Error('Could not load the payment field. Please check your connection and try again.'));
    document.head.appendChild(script);
  });
  return stripeJsPromise;
}

/**
 * Mounts a single Stripe Card Element into the returned `containerRef` once
 * `publishableKey` is set, and exposes `createPaymentMethod()` to turn the
 * entered card into an opaque `payment_method_id` — this app's own backend
 * never receives a raw card number; Stripe.js sends it directly to Stripe
 * from the browser. Pass `null`/`undefined` for `publishableKey` to render
 * nothing (the default — used whenever `require_card_for_signup` is off).
 */
export function useStripeCardElement(publishableKey) {
  const containerRef = useRef(null);
  const stripeRef = useRef(null);
  const cardRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [cardError, setCardError] = useState('');
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setReady(false);

    if (!publishableKey || !containerRef.current) return undefined;

    loadStripeJs()
      .then((Stripe) => {
        if (cancelled || !containerRef.current) return;
        const stripe = Stripe(publishableKey);
        const elements = stripe.elements();
        const card = elements.create('card', {
          style: { base: { fontSize: '16px', fontFamily: 'inherit' } },
        });
        card.mount(containerRef.current);
        card.on('change', (event) => setCardError(event.error ? event.error.message : ''));
        stripeRef.current = stripe;
        cardRef.current = card;
        setReady(true);
      })
      .catch((e) => setLoadError(e.message || 'Could not load the payment field.'));

    return () => {
      cancelled = true;
      if (cardRef.current) {
        cardRef.current.unmount();
        cardRef.current = null;
      }
      stripeRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishableKey]);

  const createPaymentMethod = useCallback(async (billingDetails) => {
    if (!stripeRef.current || !cardRef.current) {
      throw new Error('Enter your card details first.');
    }
    const { paymentMethod, error } = await stripeRef.current.createPaymentMethod({
      type: 'card',
      card: cardRef.current,
      billing_details: billingDetails,
    });
    if (error) throw new Error(error.message);
    return paymentMethod.id;
  }, []);

  return { containerRef, ready, cardError, loadError, createPaymentMethod };
}

export default useStripeCardElement;
